import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { logoutUser } from "../../../../slices/auth/authenticationSlice";
import { getLmsRole, isParentRole } from "../../utils/lmsRole";
import AssignmentBuilder from "../components/AssignmentBuilder";
import AssignmentCard from "../components/AssignmentCard";
import AssignmentsEmptyState from "../components/AssignmentsEmptyState";
import { AssignmentManagementWrapper } from "../styles/AssignmentManagementStyle";
import {
    getAssignmentErrorMessage,
    normalizeAssignmentStatus,
    normalizeAssignmentsResponse,
    normalizeContentOption,
    normalizeStudentOption,
} from "../utils/assignmentUtils";
import {
    createAssignment,
    getAssignments,
} from "../controllers/assignmentController";
import { getContents, getContentById } from "../../content/controllers/contentController";
import { getStudents } from "../../students/controllers/studentController";
import { normalizeContentListResponse, normalizeSingleContentResponse } from "../../content/utils/contentUtils";

const BOOK_PLACEHOLDER_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="220" height="280" viewBox="0 0 220 280">
  <defs>
    <linearGradient id="cover" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#edf4f1" />
      <stop offset="100%" stop-color="#dbe9e4" />
    </linearGradient>
  </defs>
  <rect width="220" height="280" fill="url(#cover)" />
  <rect x="30" y="30" width="160" height="220" rx="12" fill="#ffffff" stroke="#cddbd5" />
  <rect x="44" y="46" width="16" height="188" rx="6" fill="#1d6257" />
  <rect x="70" y="84" width="108" height="12" rx="6" fill="#d7e7e1" />
  <rect x="70" y="106" width="98" height="10" rx="5" fill="#e6f0ec" />
  <rect x="70" y="125" width="84" height="10" rx="5" fill="#e6f0ec" />
  <text x="124" y="182" fill="#2f5850" font-size="28" font-family="Arial, sans-serif" font-weight="700" text-anchor="middle">BOOK</text>
</svg>
`)}`;

const AssignmentManagementPage = () => {
    const dispatch = useDispatch();
    const { credObj } = useSelector((store) => store.auth);
    const { currentUser } = useSelector((store) => store.users);
    const role = getLmsRole({ currentUser, credObj });
    const isParent = isParentRole(role);
    const [activeView, setActiveView] = useState("create");

    const [contentOptions, setContentOptions] = useState([]);
    const [studentOptions, setStudentOptions] = useState([]);
    const [assignments, setAssignments] = useState([]);

    const [isLoadingContent, setIsLoadingContent] = useState(true);
    const [isLoadingStudents, setIsLoadingStudents] = useState(true);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);

    const [createError, setCreateError] = useState("");
    const [listError, setListError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const [selectedFilterStudentId, setSelectedFilterStudentId] = useState("");
    const [previewItem, setPreviewItem] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);

    const handleUnauthorized = useCallback(() => {
        dispatch(logoutUser());
        window.location.assign("/lms/login");
    }, [dispatch]);

    const withApiErrorHandling = useCallback(
        (apiError, setError) => {
            if (apiError?.status === 401) {
                handleUnauthorized();
                return;
            }

            const message = getAssignmentErrorMessage(apiError);
            setError(message);
        },
        [handleUnauthorized]
    );

    const loadAssignmentList = useCallback(
        async (studentId = "") => {
            setIsLoadingAssignments(true);
            setListError("");
            try {
                const response = await getAssignments({ studentId: studentId || undefined });
                setAssignments(normalizeAssignmentsResponse(response));
            } catch (apiError) {
                withApiErrorHandling(apiError, setListError);
            } finally {
                setIsLoadingAssignments(false);
            }
        },
        [withApiErrorHandling]
    );

    useEffect(() => {
        let isMounted = true;

        const loadContent = async () => {
            setIsLoadingContent(true);
            try {
                const response = await getContents();
                const normalized = normalizeContentListResponse(response).map(normalizeContentOption);
                if (isMounted) {
                    setContentOptions(normalized);
                }
            } catch (apiError) {
                if (isMounted) withApiErrorHandling(apiError, setCreateError);
            } finally {
                if (isMounted) setIsLoadingContent(false);
            }
        };

        const loadStudentsList = async () => {
            setIsLoadingStudents(true);
            try {
                const response = await getStudents();
                const students = (response?.data?.students || []).map(normalizeStudentOption);
                if (isMounted) {
                    setStudentOptions(students);
                }
            } catch (apiError) {
                if (isMounted) withApiErrorHandling(apiError, setCreateError);
            } finally {
                if (isMounted) setIsLoadingStudents(false);
            }
        };

        loadContent();
        loadStudentsList();

        return () => {
            isMounted = false;
        };
    }, [withApiErrorHandling]);

    useEffect(() => {
        if (activeView !== "tracking") return;
        loadAssignmentList(selectedFilterStudentId);
    }, [activeView, selectedFilterStudentId, loadAssignmentList]);

    const handleCreateAssignment = async (payload) => {
        setCreateError("");
        setIsSubmitting(true);

        try {
            await createAssignment(payload);
            toast.success("Reading challenge assigned successfully.");
            setResetKey((current) => current + 1);
            await loadAssignmentList(selectedFilterStudentId);
        } catch (apiError) {
            withApiErrorHandling(apiError, setCreateError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePreviewContent = async (contentId) => {
        setCreateError("");
        setIsPreviewOpen(true);
        setIsPreviewLoading(true);
        setPreviewItem(null);

        try {
            const response = await getContentById(contentId);
            setPreviewItem(normalizeSingleContentResponse(response));
        } catch (apiError) {
            withApiErrorHandling(apiError, setCreateError);
            setIsPreviewOpen(false);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    return (
        <AssignmentManagementWrapper>
            {activeView === "create" && (
                <>
                    <div className="assignment-view-actions">
                        <button
                            type="button"
                            className="assignment-view-btn"
                            onClick={() => setActiveView("tracking")}
                        >
                            Track Assignment
                        </button>
                    </div>

                    <AssignmentBuilder
                        contentOptions={contentOptions}
                        studentOptions={studentOptions}
                        isLoadingContent={isLoadingContent}
                        isLoadingStudents={isLoadingStudents}
                        createError={createError}
                        isSubmitting={isSubmitting}
                        onCreate={handleCreateAssignment}
                        onPreviewContent={handlePreviewContent}
                        resetKey={resetKey}
                        isParent={isParent}
                    />
                </>
            )}

            {activeView === "tracking" && (
                <section className="assignments-list-panel">
                    <div className="assignment-view-actions tracking-view-actions">
                        <button
                            type="button"
                            className="assignment-view-btn ghost"
                            onClick={() => setActiveView("create")}
                        >
                            Go Back
                        </button>
                    </div>

                    <div className="assignments-list-top">
                        <div>
                            <p className="eyebrow">Assignment Tracking</p>
                            <h2>Track reading challenge progress</h2>
                        </div>
                        <label className="lms-field">
                            <span>{isParent ? "Filter by kid" : "Filter by student"}</span>
                            <select
                                value={selectedFilterStudentId}
                                onChange={(event) => setSelectedFilterStudentId(event.target.value)}
                            >
                                <option value="">{isParent ? "All Kids" : "All Students"}</option>
                                {studentOptions.map((student) => (
                                    <option key={student.id} value={student.id}>
                                        {student.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {listError && (
                        <p className="assignments-error" role="alert">
                            {listError}
                        </p>
                    )}

                    {isLoadingAssignments && <p className="assignments-loading">Loading assignments...</p>}

                    {!isLoadingAssignments && !listError && assignments.length === 0 && (
                        <AssignmentsEmptyState isParent={isParent} />
                    )}

                    {!isLoadingAssignments && !listError && assignments.length > 0 && (
                        <section className="assignments-list" aria-label="Assignment list">
                            {assignments.map((item) => (
                                <AssignmentCard key={`${item.id}-${normalizeAssignmentStatus(item.status)}`} item={item} />
                            ))}
                        </section>
                    )}
                </section>
            )}

            {isPreviewOpen && (
                <div className="assignment-sheet-overlay" role="presentation" onClick={() => setIsPreviewOpen(false)}>
                    <section
                        className="assignment-sheet preview-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Preview reading content"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <header className="assignment-sheet-header">
                            <h3>{previewItem?.title || "Reading content preview"}</h3>
                            <button
                                type="button"
                                className="assignment-sheet-close"
                                onClick={() => setIsPreviewOpen(false)}
                            >
                                Close
                            </button>
                        </header>

                        <div className="assignment-sheet-body">
                            {isPreviewLoading && <p className="assignments-loading">Loading preview...</p>}

                            {!isPreviewLoading && previewItem && (
                                <>
                                    <div className="assignment-preview-cover" aria-hidden="true">
                                        <img
                                            src={
                                                previewItem.imageUrl && previewItem.imageUrl !== "no-image.jpg"
                                                    ? previewItem.imageUrl
                                                    : BOOK_PLACEHOLDER_IMAGE
                                            }
                                            alt=""
                                            loading="lazy"
                                            onError={(event) => {
                                                if (event.currentTarget.src !== BOOK_PLACEHOLDER_IMAGE) {
                                                    event.currentTarget.src = BOOK_PLACEHOLDER_IMAGE;
                                                }
                                            }}
                                        />
                                    </div>

                                    <p className="assignment-preview-meta">
                                        {previewItem.author || "ReadWell"} • Level {previewItem.level || "A"} •{" "}
                                        {previewItem.difficulty || "MEDIUM"}
                                    </p>

                                    <p className="assignment-preview-meta">
                                        Categories: {previewItem.categories?.length ? previewItem.categories.join(", ") : "Uncategorized"}
                                    </p>

                                    <ol className="assignment-preview-paragraphs">
                                        {previewItem.paragraphs?.map((paragraph) => (
                                            <li key={paragraph.id}>{paragraph.paragraph}</li>
                                        ))}
                                    </ol>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            )}
        </AssignmentManagementWrapper>
    );
};

export default AssignmentManagementPage;
